import React, { useState, useEffect } from 'react';
import { Toast } from '../../components/common/Toast';
import SectionCard from '../../components/common/SectionCard';
import { HideLicense, LicenseActivationFailedIcon, LicenseActivationIcon, LicenseIcon, ShowLicense } from '../../components/common/SvgIcon';
import MainPageLayout from '../../components/common/MainPageLayout';
import ConfirmationModal from '../../components/common/ConfirmationModal';

const License = () => {
  const [licenseKey, setLicenseKey]       = useState('');
  const [licenseStatus, setLicenseStatus] = useState('');
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [deactivating, setDeactivating]   = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showLicense, setShowLicense]     = useState(false);

  const isValid = licenseStatus === 'valid';

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res    = await fetch(`${LEGAL_PAGES.apiUrl}/license-status`, {
        headers: { 'X-WP-Nonce': LEGAL_PAGES.nonce },
      });
      const result = await res.json();
      if (result.success && result.data) {
        setLicenseKey(result.data.license_key || '');
        setLicenseStatus(result.data.license_status || '');
      }
    } catch (e) {
      Toast.error('Failed to load license info.');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!licenseKey.trim()) { Toast.error('Please enter a license key.'); return; }
    setSaving(true);
    try {
      const res    = await fetch(`${LEGAL_PAGES.apiUrl}/license-activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': LEGAL_PAGES.nonce },
        body: JSON.stringify({ license_key: licenseKey }),
      });
      const result = await res.json();
      if (result.success) {
        setLicenseStatus(result.data.license_status);
        Toast.success(result.message || 'License activated!');
        window.location.reload();
      } else {
        Toast.error(result.message || 'Activation failed.');
      }
    } catch (e) {
      Toast.error('An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      const res    = await fetch(`${LEGAL_PAGES.apiUrl}/license-deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': LEGAL_PAGES.nonce },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      if (result.success) {
        setLicenseStatus('');
        setLicenseKey('');
        setShowDeactivateModal(false);
        Toast.success('License deactivated.');
        window.location.reload();
      } else {
        Toast.error(result.message || 'Deactivation failed.');
      }
    } catch (e) {
      Toast.error('An error occurred.');
    } finally {
      setDeactivating(false);
    }
  };

  return (

    <MainPageLayout
      title="License"
      description="Manage your Legal Pages Pro license"
    >
      <div className="space-y-6 mt-6">

        {/* ── Active status banner ── */}
        {!loading && isValid && (
          <SectionCard maxWidth='max-w-[672px]' title="License Status" icon={<LicenseIcon />}>
            <div className="flex items-center gap-4 bg-green-50 border border-green-200 rounded-2xl px-5 py-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <LicenseActivationIcon />
              </div>
              <div>
                <p className="text-sm font-bold text-green-800">License Active</p>
                <p className="text-sm text-green-700">Your Pro license is active and all features are unlocked.</p>
              </div>
            </div>
          </SectionCard>
        )}

        {/* ── License Key card ── */}
        {loading ? (
          <SectionCard maxWidth='max-w-[672px]' title="Plugin License Options" icon={<LicenseIcon />}>
            <div className="space-y-3">
              <div className="h-10 bg-gray-200 rounded animate-pulse" />
              <div className="h-10 bg-gray-200 rounded animate-pulse" />
              <div className="h-8  bg-gray-200 rounded animate-pulse w-36" />
            </div>
          </SectionCard>
        ) : (
          <SectionCard maxWidth='max-w-[672px]' title="Plugin License Options" icon={<LicenseIcon />}>
            <div className="space-y-4">

              {/* License Key field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#17171C]">
                  License Key
                </label>
                <div className="relative">
                  <input
                    type={showLicense ? 'text' : 'password'}
                    value={licenseKey}
                    onChange={(e) => setLicenseKey(e.target.value)}
                    disabled={isValid}
                    placeholder={isValid ? '••••••••••••••••••••' : 'Enter license key'}
                    className={[
                      'h-12 w-full !rounded-xl border !border-[#E4E4E7] px-4 text-sm outline-none',
                      'focus:!border-[#17171C] focus:!shadow-none focus:!ring-0',
                      isValid
                        ? 'bg-[#FAFAFA] text-[#6F6F7B] cursor-not-allowed'
                        : 'bg-white text-[#17171C]'
                    ].join(' ')}
                  />
                  {!isValid && licenseKey && (
                    <button
                      type="button"
                      onClick={() => setShowLicense(!showLicense)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showLicense ? (
                        <ShowLicense />
                      ) : (
                        <HideLicense />
                      )}
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#9CA3AF]">
                  Find your license key in your purchase confirmation email or account dashboard.
                </p>
              </div>

              {/* Action button */}
              {!isValid ? (
                <button
                  type="button"
                  onClick={handleActivate}
                  disabled={saving}
                  className={[
                    'inline-flex items-center gap-2 rounded-xl px-6 py-[14px] text-sm font-semibold text-white shadow-[0px_4px_14px_-2px_#7C3BED40] transition',
                    saving
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-[#7C3BED] hover:bg-indigo-700 cursor-pointer',
                  ].join(' ')}
                >
                  {saving ? (
                    <>
                      <LicenseActivationFailedIcon />
                      Activating...
                    </>
                  ) : 'Activate License'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDeactivateModal(true)}
                  disabled={deactivating}
                  className={[
                    'inline-flex items-center gap-2 rounded-xl px-6 py-[14px] text-sm font-semibold transition border',
                    deactivating
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-red-500 border-red-300 hover:bg-red-50 cursor-pointer',
                  ].join(' ')}
                >
                  {deactivating ? 'Deactivating...' : 'Deactivate License'}
                </button>
              )}
            </div>
          </SectionCard>
        )}

        {/* ── Note box ── */}
        {!loading && (
          <div className="w-[672px] bg-[#F9FAFB] rounded-2xl px-5 py-4">
            <p className="text-sm text-[#6F6F7B] leading-relaxed">
              <span className="font-semibold text-[#17171C]">Note:</span>{' '}
              Your license is tied to this website. If you need to transfer your license to a different
              site, please deactivate it first and then activate on the new site.
            </p>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={handleDeactivate}
        title="Deactivate License"
        message="Are you sure you want to deactivate your license on this site?"
        confirmText="Deactivate"
        cancelText="Cancel"
        isLoading={deactivating}
      />
    </MainPageLayout>
  );
};

export default License;